import { useContext } from "react";
import { useReactiveVar } from "@apollo/client";
import { CompanyAccess, PermissionScope, StorageProductRelationHistoryAction } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { WriteOffSource } from "@me-team/common/src/interfaces/interfaces";

type ScopedWriteOffAccess = {
    all: boolean;
    self: boolean;
};

type WriteOffOperationMetadata =
    | {
          historyAction?: StorageProductRelationHistoryAction | null;
          employee?: {
              id?: number | null;
          } | null;
          historyCreator?: {
              id?: number | null;
          } | null;
      }
    | null
    | undefined;

type UseWriteOffProductAccessParams = {
    writeOffSource: WriteOffSource;
    isEditMode: boolean;
    operationMetadata: WriteOffOperationMetadata;
};

const resolveScopedWriteOffAccess = (
    currentBranchEmployeeId: number | null | undefined,
    targetEmployeeId: number | null | undefined,
    access: ScopedWriteOffAccess
): boolean => {
    if (access.all) return true;
    if (!access.self || !currentBranchEmployeeId || !targetEmployeeId) return false;

    return currentBranchEmployeeId === targetEmployeeId;
};

export const useWriteOffProductAccess = ({
    writeOffSource,
    isEditMode,
    operationMetadata,
}: UseWriteOffProductAccessParams) => {
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canEditConsumablesWriteOffAll = useRoleAccess(CompanyAccess.WarehouseOperationConsumablesWriteOffEdit, {
        scope: PermissionScope.All,
    });
    const canEditConsumablesWriteOffSelf = useRoleAccess(CompanyAccess.WarehouseOperationConsumablesWriteOffEdit, {
        scope: PermissionScope.Self,
    });
    const canWriteOffLogbookConsumables = useRoleAccess(CompanyAccess.LogbookConsumablesWriteOff);

    const operationEmployeeId = operationMetadata?.historyCreator?.id;
    const isExpectedHistoryAction =
        operationMetadata?.historyAction === StorageProductRelationHistoryAction.BookingWriteOff;
    const isRoleAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const warehouseOperationEditAccess: ScopedWriteOffAccess = {
        all: canEditConsumablesWriteOffAll,
        self: canEditConsumablesWriteOffSelf,
    };
    const canEditWriteOffOperation =
        isEditMode &&
        Boolean(operationMetadata) &&
        isExpectedHistoryAction &&
        resolveScopedWriteOffAccess(currentBranchEmployeeId, operationEmployeeId, warehouseOperationEditAccess);

    let canCreateCurrentWriteOff = false;
    let canEditCurrentOperation = false;

    switch (writeOffSource) {
        case "logbook":
            canCreateCurrentWriteOff = !isEditMode && canWriteOffLogbookConsumables;
            canEditCurrentOperation = canEditWriteOffOperation;
            break;
        case "warehouseOperation":
            canEditCurrentOperation = canEditWriteOffOperation;
            break;
        default:
            break;
    }

    return {
        canCreateCurrentWriteOff,
        canEditCurrentOperation,
        isAccessPending: isRoleAccessPending,
    };
};
